import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faStar,
  faChevronDown,
  faChevronRight,
  faEye,
  faEyeSlash,
  faCalendarAlt,
  faUsers,
  faUser,
  faCheck,
  faClock,
  faHistory,
} from "@fortawesome/free-solid-svg-icons";
import { useUserById } from "../../hooks/user/useUserById";
import { useFetchUser } from "../../hooks/user/useFetchUser";
import VerificationBadge from "../user/VerificationBadge";

/**
 * LeagueScorecardView - Updated mockup-style "My League" tab for the Scorecard page.
 * Features custom dropdown selectors for league and event selection.
 */
const LeagueScorecardView = ({
  submissions,
  membersWithoutSubmission,
  leagueName,
  eventName,
  eventDate,
  isEventStarted,
  totalMembers,
  submittedCount,
  // New props for the updated UI
  leagues,
  selectedLeagueId,
  onLeagueChange,
  events,
  selectedEventId,
  onEventChange,
}) => {
  const { data: dbUser } = useFetchUser();
  const [expandedMember, setExpandedMember] = useState(dbUser?._id || null);
  const [showLeagueSelector, setShowLeagueSelector] = useState(false);
  const [showEventHistory, setShowEventHistory] = useState(false);

  const selectedEvent = events?.find(
    (e) =>
      e.EventId === selectedEventId || e.EventId === parseInt(selectedEventId),
  );

  // Determine event status
  const getEventStatus = (event) => {
    if (!event) return "upcoming";
    const eventDate = new Date(event.DateTime);
    const now = new Date();
    if (eventDate > now) return "upcoming";
    // If event started less than 24 hours ago, consider it "live"
    const hoursSinceStart = (now - eventDate) / (1000 * 60 * 60);
    if (hoursSinceStart < 24) return "live";
    return "completed";
  };

  const getEventStatusIcon = (status) => {
    switch (status) {
      case "live":
        return (
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-deep-red opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-deep-red"></span>
          </span>
        );
      case "completed":
        return (
          <FontAwesomeIcon icon={faCheck} className="w-3 h-3 text-green-500" />
        );
      default:
        return (
          <FontAwesomeIcon
            icon={faClock}
            className="w-3 h-3 text-medium-grey"
          />
        );
    }
  };

  const getEventStatusBadge = (status) => {
    switch (status) {
      case "live":
        return (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-bold bg-deep-red/10 text-deep-red border border-deep-red/20">
            {getEventStatusIcon(status)}
            <span>Live</span>
          </span>
        );
      case "completed":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-green-50 text-green-600 border border-green-200">
            {getEventStatusIcon(status)}
            <span>Final</span>
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-medium-grey">
            {getEventStatusIcon(status)}
            <span>Upcoming</span>
          </span>
        );
    }
  };

  const eventCanShowPicks = isEventStarted;

  return (
    <div className="space-y-4">
      {/* League Selector (custom dropdown) */}
      {leagues && leagues.length > 1 && (
        <div className="relative">
          <button
            onClick={() => setShowLeagueSelector(!showLeagueSelector)}
            className="w-full bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3 text-left transition-colors hover:bg-light-grey/50"
          >
            <div className="w-12 h-12 rounded-xl bg-deep-red/10 flex items-center justify-center border border-deep-red/20 text-sm font-bold text-deep-red">
              {leagueName?.[0] || "L"}
            </div>
            <div className="flex-1">
              <p className="text-[10px] text-medium-grey uppercase tracking-wider">
                Your League
              </p>
              <h2 className="font-bold text-lg text-dark-grey">
                {leagueName || "Select League"}
              </h2>
              <div className="flex items-center gap-2 text-xs text-medium-grey">
                <FontAwesomeIcon icon={faUsers} className="w-3 h-3" />
                <span>{totalMembers} members</span>
              </div>
            </div>
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`w-4 h-4 text-medium-grey transition-transform ${showLeagueSelector ? "rotate-180" : ""}`}
            />
          </button>

          {/* League Dropdown */}
          {showLeagueSelector && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-gray-100 shadow-xl z-20 overflow-hidden">
              <div className="p-2 border-b border-gray-100">
                <p className="text-xs text-medium-grey px-2">Select League</p>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {leagues.map((league) => (
                  <button
                    key={league._id}
                    onClick={() => {
                      onLeagueChange(league._id);
                      setShowLeagueSelector(false);
                    }}
                    className={`w-full p-3 flex items-center gap-3 text-left transition-colors hover:bg-light-grey/50 ${
                      selectedLeagueId === league._id ? "bg-deep-red/5" : ""
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold border ${
                        selectedLeagueId === league._id
                          ? "bg-deep-red/10 text-deep-red border-deep-red/20"
                          : "bg-gray-100 text-dark-grey border-gray-200"
                      }`}
                    >
                      {league.leagueName?.[0] || "L"}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-dark-grey text-sm">
                        {league.leagueName}
                      </p>
                    </div>
                    {selectedLeagueId === league._id && (
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="w-4 h-4 text-deep-red"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Event Selector (custom dropdown with history) */}
      {events && events.length > 0 && (
        <div className="relative">
          <button
            onClick={() => setShowEventHistory(!showEventHistory)}
            className="w-full bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden text-left"
          >
            <div className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className="w-5 h-5 text-medium-grey"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-dark-grey text-sm">
                    {eventName || "Select Event"}
                  </p>
                  {selectedEvent &&
                    getEventStatusBadge(getEventStatus(selectedEvent))}
                </div>
                <p className="text-xs text-medium-grey">{eventDate || ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <p className="text-[10px] text-medium-grey">Submissions</p>
                  <p className="text-sm font-semibold text-dark-grey">
                    {submittedCount}/{totalMembers}
                  </p>
                </div>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`w-4 h-4 text-medium-grey transition-transform ${showEventHistory ? "rotate-180" : ""}`}
                />
              </div>
            </div>
          </button>

          {/* Event History Dropdown */}
          {showEventHistory && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-gray-100 shadow-xl z-10 overflow-hidden">
              <div className="p-3 border-b border-gray-100 flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faHistory}
                  className="w-4 h-4 text-medium-grey"
                />
                <p className="text-sm font-semibold text-dark-grey">
                  Event History
                </p>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {events.map((event) => {
                  const status = getEventStatus(event);
                  const isSelected =
                    event.EventId === selectedEventId ||
                    event.EventId === parseInt(selectedEventId);
                  return (
                    <button
                      key={event.EventId}
                      onClick={() => {
                        onEventChange(event.EventId);
                        setShowEventHistory(false);
                      }}
                      className={`w-full p-3 flex items-center gap-3 text-left transition-colors hover:bg-light-grey/50 border-b border-gray-50 last:border-0 ${
                        isSelected ? "bg-deep-red/5" : ""
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                        {getEventStatusIcon(status)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-dark-grey text-sm">
                            {event.Name}
                          </p>
                          {status === "live" && (
                            <span className="text-[10px] text-deep-red font-semibold uppercase">
                              Now
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-medium-grey">
                          {new Date(event.DateTime).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                            },
                          )}
                        </p>
                      </div>
                      {isSelected && (
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          className="w-3 h-3 text-deep-red"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Member Submissions */}
      <div className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <h3 className="text-xs font-bold text-medium-grey uppercase tracking-wide">
            Member Submissions
          </h3>
          {!eventCanShowPicks && (
            <span className="text-xs text-medium-grey flex items-center gap-1">
              <FontAwesomeIcon icon={faEyeSlash} className="w-3 h-3" />
              Hidden until event
            </span>
          )}
        </div>

        {/* Submitted members */}
        {submissions &&
          submissions.length > 0 &&
          submissions.map((submission, index) => {
            const userId =
              typeof submission.userId === "object" &&
              submission.userId !== null
                ? submission.userId._id
                : submission.userId;

            return (
              <MemberSubmissionCard
                key={userId || index}
                submission={submission}
                eventCanShowPicks={eventCanShowPicks}
                isExpanded={expandedMember === userId}
                onToggle={() =>
                  setExpandedMember(expandedMember === userId ? null : userId)
                }
                currentUserId={dbUser?._id}
              />
            );
          })}

        {/* Members without submission */}
        {membersWithoutSubmission &&
          membersWithoutSubmission.length > 0 &&
          membersWithoutSubmission.map((member) => (
            <NoSubmissionCard
              key={member.userId}
              userId={member.userId}
              user={member.user}
            />
          ))}

        {(!submissions || submissions.length === 0) &&
          (!membersWithoutSubmission ||
            membersWithoutSubmission.length === 0) && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
              <p className="text-sm text-medium-grey">
                No submissions found for this event
              </p>
            </div>
          )}
      </div>
    </div>
  );
};

/**
 * Individual member submission card with expandable lineup
 */
const MemberSubmissionCard = ({
  submission,
  eventCanShowPicks,
  isExpanded,
  onToggle,
  currentUserId,
}) => {
  const navigate = useNavigate();

  const userId =
    typeof submission.userId === "object" && submission.userId !== null
      ? submission.userId._id
      : submission.userId;

  const populatedUser =
    typeof submission.userId === "object" && submission.userId !== null
      ? submission.userId
      : null;

  const { data: fetchedUser } = useUserById(populatedUser ? null : userId);
  const user = populatedUser || fetchedUser;

  const displayName = user?.username || user?.name || "Loading...";
  const isYou = userId === currentUserId;

  const selections = submission.selections || [];
  const alternates = submission.alternates || [];
  const projectedTotal = submission.projectedTotal || 0;
  const totalScore = submission.total || 0;

  // Find LOTN fighter
  const lotnFighter = selections.find((s) => s.lotn || s.lastUpdate?.isLotn);

  const getStatusStyle = (selection) => {
    const outcome = selection.lastUpdate?.outcome || "Pending";
    if (outcome.toLowerCase().includes("win"))
      return "bg-green-100 text-green-700 border-green-200";
    if (outcome.toLowerCase().includes("loss"))
      return "bg-red-100 text-red-700 border-red-200";
    if (eventCanShowPicks && outcome === "Pending")
      return "bg-deep-red/10 text-deep-red border-deep-red/20";
    return "bg-gray-100 text-medium-grey border-gray-200";
  };

  const getStatusLabel = (selection) => {
    const outcome = selection.lastUpdate?.outcome || "Pending";
    if (outcome.toLowerCase().includes("win")) return "Won";
    if (outcome.toLowerCase().includes("loss")) return "Lost";
    if (eventCanShowPicks && outcome === "Pending") return "Live";
    return "Pending";
  };

  return (
    <div
      className={`bg-white rounded-xl border overflow-hidden transition-all ${
        isYou
          ? "border-deep-red/30 ring-1 ring-deep-red/10"
          : "border-gray-100 shadow-sm"
      }`}
    >
      {/* Header Row */}
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center gap-3 text-left"
      >
        <div
          className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 overflow-hidden cursor-pointer ${
            isYou
              ? "bg-deep-red/10 border-2 border-deep-red/30"
              : "bg-gray-100 border border-gray-200"
          }`}
          onClick={(e) => {
            e.stopPropagation();
            if (userId) navigate(`/users/${userId}`);
          }}
        >
          {user?.picture ? (
            <img
              src={user.picture}
              alt={displayName}
              className="w-full h-full object-cover"
            />
          ) : (
            <FontAwesomeIcon
              icon={faUser}
              className={`w-4 h-4 ${isYou ? "text-deep-red/60" : "text-gray-400"}`}
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-dark-grey truncate text-sm">
              {displayName}
            </span>
            {isYou && (
              <span className="inline-flex items-center px-1.5 py-0 rounded text-[10px] font-bold border border-deep-red/30 text-deep-red">
                You
              </span>
            )}
            {user?.verified && (
              <VerificationBadge
                verified={user.verified}
                size="small"
                showTooltip={false}
              />
            )}
          </div>
          <div className="text-xs text-medium-grey mt-0.5">
            {eventCanShowPicks ? (
              <span className="flex items-center gap-1 text-green-600">
                <FontAwesomeIcon icon={faEye} className="w-3 h-3" />
                {selections.length} picks
                {totalScore !== 0 && (
                  <span className="ml-1 font-bold">
                    • {totalScore >= 0 ? "+" : ""}
                    {totalScore.toFixed(2)} pts
                  </span>
                )}
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <FontAwesomeIcon icon={faLock} className="w-3 h-3" />
                Proj: {projectedTotal.toFixed(2)} pts
              </span>
            )}
          </div>
        </div>

        <FontAwesomeIcon
          icon={faChevronDown}
          className={`w-4 h-4 text-medium-grey transition-transform ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Expanded - Lineup Details */}
      {isExpanded && eventCanShowPicks && (
        <div className="px-4 pb-4 space-y-4">
          {lotnFighter && (
            <div className="bg-gradient-to-r from-gold/10 to-gold/5 rounded-xl p-3 border border-gold/20">
              <div className="flex items-center gap-2 mb-3">
                <FontAwesomeIcon icon={faStar} className="w-4 h-4 text-gold" />
                <span className="text-xs font-bold text-gold uppercase tracking-wide">
                  Lock of the Night
                </span>
                <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gold/20 text-gold border border-gold/30">
                  2x PTS
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center border border-gold/20">
                  <FontAwesomeIcon
                    icon={faUser}
                    className="w-5 h-5 text-gold/60"
                  />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-sm text-dark-grey">
                    {lotnFighter.fighter}
                  </p>
                  <p className="text-xs text-medium-grey">
                    {lotnFighter.opponent ? `vs ${lotnFighter.opponent}` : ""}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold capitalize border ${getStatusStyle(lotnFighter)}`}
                >
                  {getStatusLabel(lotnFighter) === "Live" && (
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-deep-red opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-deep-red"></span>
                    </span>
                  )}
                  {getStatusLabel(lotnFighter)}
                </span>
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-bold text-medium-grey mb-2 uppercase tracking-wide">
              Selections
            </p>
            <div className="space-y-2">
              {selections.map((selection, idx) => (
                <div
                  key={selection._id || idx}
                  className="flex items-center gap-3 p-2 rounded-lg bg-light-grey/50 border border-gray-100"
                >
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon
                      icon={faUser}
                      className="w-4 h-4 text-gray-400"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-dark-grey truncate">
                      {selection.fighter}
                    </p>
                    <p className="text-xs text-medium-grey">
                      {selection.opponent
                        ? `vs ${selection.opponent}`
                        : `Pick #${idx + 1}`}
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold capitalize border ${getStatusStyle(selection)}`}
                  >
                    {getStatusLabel(selection) === "Live" && (
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-deep-red opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-deep-red"></span>
                      </span>
                    )}
                    {getStatusLabel(selection)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {alternates.length > 0 && (
            <div>
              <p className="text-xs font-bold text-medium-grey mb-2 uppercase tracking-wide">
                Alternates
              </p>
              <div className="space-y-2">
                {alternates.map((alt, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-2 rounded-lg bg-gray-50/50 border border-dashed border-gray-200 opacity-70"
                  >
                    <div className="w-10 h-10 rounded-lg bg-gray-100/50 flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon
                        icon={faUser}
                        className="w-4 h-4 text-gray-300"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-medium-grey truncate">
                        {alt.fighter}
                      </p>
                      <p className="text-xs text-medium-grey">
                        {alt.opponent ? `vs ${alt.opponent}` : ""}
                      </p>
                    </div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border border-gray-200 text-medium-grey">
                      Bench
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pre-event hidden state */}
      {isExpanded && !eventCanShowPicks && (
        <div className="px-4 pb-4">
          <div className="flex flex-col items-center justify-center py-8 text-center bg-light-grey rounded-xl border border-dashed border-gray-200">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <FontAwesomeIcon
                icon={faLock}
                className="w-5 h-5 text-medium-grey"
              />
            </div>
            <p className="font-medium text-sm text-dark-grey">Picks Hidden</p>
            <p className="text-xs text-medium-grey mt-1">
              Visible once event starts
            </p>
            <div className="mt-4 px-4 py-2 bg-white rounded-lg border border-gray-100">
              <p className="text-xs text-medium-grey">Projected Total</p>
              <p className="font-bold text-lg text-dark-grey">
                {projectedTotal.toFixed(2)} pts
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Card for members who haven't submitted
 */
const NoSubmissionCard = ({ userId, user: userProp }) => {
  const navigate = useNavigate();
  // Use the embedded member user from the league-members payload; only fetch
  // as a fallback so the non-submitters list doesn't fire one request per row.
  const { data: fetchedUser, isLoading: fetching } = useUserById(
    userProp ? undefined : userId,
  );
  const user = userProp || fetchedUser;
  const isLoading = !userProp && fetching;

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 animate-pulse">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-gray-200" />
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-24 mb-1" />
            <div className="h-3 bg-gray-100 rounded w-32" />
          </div>
        </div>
      </div>
    );
  }

  const displayName = user?.username || user?.name || "Unknown User";

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
      <div className="flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden cursor-pointer"
          onClick={() => userId && navigate(`/users/${userId}`)}
        >
          {user?.picture ? (
            <img
              src={user.picture}
              alt={displayName}
              className="w-full h-full object-cover"
            />
          ) : (
            <FontAwesomeIcon icon={faUser} className="w-4 h-4 text-gray-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-dark-grey truncate text-sm">
              {displayName}
            </span>
            {user?.verified && (
              <VerificationBadge
                verified={user.verified}
                size="small"
                showTooltip={false}
              />
            )}
          </div>
          <span className="text-xs text-red-500">No submission</span>
        </div>
      </div>
    </div>
  );
};

export default LeagueScorecardView;
